import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, FormControl, IconButton, Tooltip } from '@mui/material';
import { CancelOutlined, CheckCircleOutlined, EditOutlined, RefreshOutlined } from '@mui/icons-material';
import { GridFilterModel, GridFooterContainer, GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro';
import { CustomPagination } from '../../components/CDGrid';

import Heading, { SubHeading } from '../../components/Heading';
import CDGridPro from '../../components/CDGridPro';
import Block from '../../components/Block';
import Column from '../../components/Column';
import Row from '../../components/Row';
import usePageTitle from '../../hooks/usePageTitle';
import ApiService, { AdvertiserActivityResponse } from '../../ApiService';
import AdvertiserActivityExportCsvButton from './AdvertiserActivityExportCsvButton';
import AdvertiserAutocomplete from '../../components/AdvertiserAutocomplete';
import AgencyAutocomplete from '../../components/AgencyAutocomplete';
import { Dealer as Advertiser } from '../../types/Dealer';
import { Agency } from '../../types/Agency';
import { AdvertiserActivityRow } from '../../types/AdvertiserActivity';

const BooleanCell = ({ value }: { value: boolean }) => {
    return value ? <CheckCircleOutlined color="success" /> : <CancelOutlined color="error" />;
};

const GridFooter = () => (
    <GridFooterContainer>
        <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 1, fontSize: '0.8rem', color: 'text.secondary' }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckCircleOutlined color="success" fontSize="small" /> Activity
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CancelOutlined color="error" fontSize="small" /> No Activity
            </Box>
        </Box>
        <CustomPagination />
    </GridFooterContainer>
);

const AdvertiserActivityPage = () => {
    usePageTitle({ segments: ['Advertiser Activity'] });
    const navigate = useNavigate();
    const [rows, setRows] = useState<AdvertiserActivityRow[] | null>(null);
    const [allRows, setAllRows] = useState<AdvertiserActivityRow[] | null>(null);
    const [lastRefreshedAt, setLastRefreshedAt] = useState<string | null>(null);
    const [refreshing, setRefreshing] = useState(false);
    const [selectedAdvertiserIds, setSelectedAdvertiserIds] = useState<number[]>([]);
    const [selectedAgencyIds, setSelectedAgencyIds] = useState<number[]>([]);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 20 });
    const [sortModel, setSortModel] = useState<GridSortModel>([]);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({
        items: [
            { field: 'mediaStudio', operator: 'contains', value: 'false' },
            { field: 'hasRecentAdTag', operator: 'contains', value: 'false' },
        ],
    });

    const applyActivityResponse = useCallback((response: AdvertiserActivityResponse) => {
        const mapped = response.data.map((d) => ({
            id: d.id,
            agencyId: d.agencyId,
            agency: d.agencyName,
            advertiser: d.advertiserName,
            mediaStudio: d.mediaStudio,
            hasRecentAdTag: d.hasRecentAdTag,
        }));
        setAllRows(mapped);
        setLastRefreshedAt(response.lastRefreshedAt);
    }, []);

    useEffect(() => {
        ApiService.getAdvertiserActivity()
            .then((response) => applyActivityResponse(response.data))
            .catch(() => {
                setAllRows([]);
                setRows([]);
            });
    }, [applyActivityResponse]);

    useEffect(() => {
        if (allRows === null) return;
        let filtered = allRows;
        if (selectedAgencyIds.length > 0) {
            filtered = filtered.filter((row) => selectedAgencyIds.includes(row.agencyId));
        }
        if (selectedAdvertiserIds.length > 0) {
            filtered = filtered.filter((row) => selectedAdvertiserIds.includes(row.id));
        }
        setRows(filtered);
    }, [selectedAdvertiserIds, selectedAgencyIds, allRows]);

    const [canRefresh, setCanRefresh] = useState(false);

    useEffect(() => {
        const check = () => {
            if (!lastRefreshedAt) return setCanRefresh(false);
            const elapsed = Date.now() - new Date(lastRefreshedAt).getTime();
            setCanRefresh(elapsed >= 10 * 60 * 1000);
        };
        check();
        const interval = setInterval(check, 30_000);
        return () => clearInterval(interval);
    }, [lastRefreshedAt]);

    const handleRefresh = useCallback(() => {
        if (!canRefresh || refreshing) return;
        setRefreshing(true);
        ApiService.refreshAdvertiserActivity()
            .then((response) => applyActivityResponse(response.data))
            .catch((error) => {
                if (error?.response?.status === 425) {
                    return ApiService.getAdvertiserActivity().then((response) => applyActivityResponse(response.data));
                }
            })
            .finally(() => setRefreshing(false));
    }, [canRefresh, refreshing, applyActivityResponse]);

    const handleAgencyOnChange = useCallback((agencies: Agency[]) => {
        const ids = agencies.map((a) => a.id as number);
        setSelectedAgencyIds(ids);
    }, []);

    const handleAdvertiserOnChange = useCallback((advertisers: Advertiser[]) => {
        const ids = advertisers.map((a) => a.id as number);
        setSelectedAdvertiserIds(ids);
    }, []);

    function handleSortModelChange(newSortModel: GridSortModel) {
        setSortModel(newSortModel);
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }

    const visibleRows = useMemo(() => {
        if (!rows) return [];
        return rows.filter((row) => {
            return filterModel.items.every((item) => {
                if (item.value === undefined || item.value === '') return true;
                const fieldValue = String(row[item.field as keyof AdvertiserActivityRow] ?? '');
                if (item.operator === 'contains') {
                    return fieldValue.includes(item.value);
                }
                if (item.operator === 'equals') {
                    return fieldValue === item.value;
                }
                return true;
            });
        });
    }, [rows, filterModel]);

    return (
        <Column gap={0} flex={1}>
            <Block>
                <Column gap={1}>
                    <Heading>Advertiser Activity</Heading>
                    <SubHeading>
                        Active advertisers with no platform activity in the last 90 days. Inactive advertisers are
                        excluded. Consider setting inactive advertisers to INACTIVE after review.
                        {lastRefreshedAt && (
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                Last refreshed: {new Date(lastRefreshedAt).toLocaleString()}
                                <Tooltip title={canRefresh ? 'Refresh data' : 'Refresh available every 10 minutes'}>
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={handleRefresh}
                                            disabled={!canRefresh || refreshing}
                                        >
                                            <RefreshOutlined
                                                fontSize="small"
                                                sx={{
                                                    animation: refreshing ? 'spin 1s linear infinite' : 'none',
                                                    '@keyframes spin': {
                                                        '0%': { transform: 'rotate(0deg)' },
                                                        '100%': { transform: 'rotate(360deg)' },
                                                    },
                                                }}
                                            />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        )}
                    </SubHeading>
                </Column>
            </Block>

            <Block flex={1}>
                <Column gap={2} sx={{ height: '100%' }}>
                    <Row justifyContent="space-between">
                        <Row justifyContent="flex-start" width="auto" gap={2}>
                            <FormControl sx={{ minWidth: 350 }}>
                                <AgencyAutocomplete
                                    multiple
                                    size="small"
                                    value={selectedAgencyIds}
                                    onChange={handleAgencyOnChange}
                                />
                            </FormControl>
                            <FormControl sx={{ minWidth: 350 }}>
                                <AdvertiserAutocomplete
                                    multiple
                                    size="small"
                                    value={selectedAdvertiserIds}
                                    onChange={handleAdvertiserOnChange}
                                />
                            </FormControl>
                        </Row>
                        <AdvertiserActivityExportCsvButton rows={visibleRows} />
                    </Row>

                    <CDGridPro
                        showToolbar={false}
                        slots={{ footer: GridFooter }}
                        sortingMode="client"
                        sortModel={sortModel}
                        onSortModelChange={handleSortModelChange}
                        filterModel={filterModel}
                        onFilterModelChange={setFilterModel}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        columns={[
                            {
                                flex: 1,
                                field: 'agency',
                                headerName: 'AGENCY',
                                headerClassName: 'grid-header',
                            },
                            {
                                flex: 1,
                                field: 'advertiser',
                                headerName: 'ADVERTISER',
                                headerClassName: 'grid-header',
                            },
                            {
                                width: 200,
                                field: 'mediaStudio',
                                headerName: 'MEDIA STUDIO',
                                headerClassName: 'grid-header',
                                headerAlign: 'center',
                                align: 'center',
                                renderCell: (params) => <BooleanCell value={params.value} />,
                            },
                            {
                                width: 200,
                                field: 'hasRecentAdTag',
                                headerName: 'AD SERVING',
                                headerClassName: 'grid-header',
                                headerAlign: 'center',
                                align: 'center',
                                renderCell: (params) => <BooleanCell value={params.value} />,
                            },
                            {
                                width: 100,
                                field: 'action',
                                headerName: 'ACTION',
                                headerClassName: 'grid-header',
                                headerAlign: 'center',
                                align: 'center',
                                sortable: false,
                                renderCell: (params) => (
                                    <Tooltip title="Edit">
                                        <IconButton
                                            size="small"
                                            aria-label="edit"
                                            onClick={() => navigate('/advertisers/' + params.row.id + '/settings')}
                                        >
                                            <EditOutlined fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                ),
                            },
                        ]}
                        rows={rows ?? []}
                        loading={rows === null}
                        autoPageSize={true}
                    />
                </Column>
            </Block>
        </Column>
    );
};

export default AdvertiserActivityPage;
